import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  TextField,
  IconButton,
  CircularProgress
} from '@mui/material';
import NotesIcon from '@mui/icons-material/Notes';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { useUpdateWorkOrder } from '@api/enterprise/workorder/mutations';

type NotesSectionProps = {
  notes: string;
  isDescription?: boolean;
  editable?: boolean;
  jobNumber?: string;
};

export const NotesSection: React.FC<NotesSectionProps> = ({
  notes,
  isDescription = false,
  editable = false,
  jobNumber
}) => {
  const { t } = useTranslation('workOrders');
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(notes);

  const { mutate: updateWorkOrder, isPending } = useUpdateWorkOrder({
    onSuccess: () => setIsEditing(false)
  });

  const title = isDescription
    ? t('workOrder.description.title')
    : t('workOrder.notes.title');

  const canEdit = editable && !isDescription && jobNumber;

  const handleEdit = () => {
    setEditValue(notes);
    setIsEditing(true);
  };

  const handleCancel = () => {
    setEditValue(notes);
    setIsEditing(false);
  };

  const handleSave = () => {
    if (!jobNumber) return;
    updateWorkOrder({
      jobNumber,
      command: { notes: editValue }
    });
  };

  return (
    <Box sx={{ mt: 2, pt: 1.5, borderTop: 1, borderColor: 'divider' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 1
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <NotesIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
          <Typography
            variant="overline"
            color="text.secondary"
            sx={{ fontSize: '0.65rem', fontWeight: 600 }}
          >
            {title}
          </Typography>
        </Box>
        {canEdit && !isEditing && (
          <IconButton size="small" onClick={handleEdit} sx={{ p: 0.5 }}>
            <EditIcon sx={{ fontSize: 16 }} />
          </IconButton>
        )}
        {isEditing && (
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            {isPending ? (
              <CircularProgress size={20} />
            ) : (
              <>
                <IconButton
                  size="small"
                  onClick={handleSave}
                  color="primary"
                  sx={{ p: 0.5 }}
                >
                  <CheckIcon sx={{ fontSize: 16 }} />
                </IconButton>
                <IconButton size="small" onClick={handleCancel} sx={{ p: 0.5 }}>
                  <CloseIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </>
            )}
          </Box>
        )}
      </Box>
      {isEditing ? (
        <TextField
          fullWidth
          multiline
          minRows={2}
          maxRows={6}
          value={editValue}
          onChange={e => setEditValue(e.target.value)}
          disabled={isPending}
          size="small"
          autoFocus
        />
      ) : (
        <Box
          sx={{
            pl: 0.5,
            maxHeight: notes.length > 300 ? '120px' : 'auto',
            overflowY: notes.length > 300 ? 'auto' : 'visible',
            '&::-webkit-scrollbar': {
              width: '6px'
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: 'rgba(0,0,0,0.05)',
              borderRadius: '3px'
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'rgba(0,0,0,0.2)',
              borderRadius: '3px',
              '&:hover': {
                backgroundColor: 'rgba(0,0,0,0.3)'
              }
            }
          }}
        >
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
            {notes || '-'}
          </Typography>
        </Box>
      )}
    </Box>
  );
};
